import {Inject, Injectable, NgZone, Renderer2, signal} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {LoadedScripts} from '../types/LoadedScripts';
import {ToolsService} from './tools.service';
import {AppConfigService} from './app-config.service';

@Injectable({
  providedIn: 'root',
})
export class CodeSnippetsService {
  public loadedScripts = signal<LoadedScripts>({});

  private headScripts: any[] = [];
  private bodyScripts: any[] = [];

  constructor(
    private tools: ToolsService,
    @Inject(DOCUMENT) private readonly document: Document,
    private appConfigService: AppConfigService,
    private ngZone: NgZone,
  ) {
    this.createScriptsList();
  }

  public loadExternalScript(renderer: Renderer2, scriptUrl: string, name: string): void {
    if (!this.isExternalScriptLoaded(name)) {
      const scriptElement: HTMLScriptElement = this.addExternalScriptToHead(renderer, scriptUrl);

      scriptElement.onload = () => {
        this.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            this.ngZone.run(() => {
              const newScript = {};
              newScript[name] = true;
              this.loadedScripts.update((scripts) => ({...scripts, ...newScript}));
            });
          }, 500);
        });
      };
    }
  }

  public addScriptsToHead(): void {
    const head = this.document.getElementsByTagName('head')[0];
    this.headScripts.forEach((scriptItem) => {
      head.appendChild(scriptItem);
    });
  }

  public addScriptToHead(script: any): void {
    const head = this.document.getElementsByTagName('head')[0];
    head.appendChild(script);
  }

  public addScriptsToBody(): void {
    const body = this.document.getElementsByTagName('body')[0];
    this.bodyScripts.forEach((scriptItem) => {
      body.appendChild(scriptItem);
    });
  }

  public addScriptToBody(script: any): void {
    const body = this.document.getElementsByTagName('body')[0];
    body.appendChild(script);
  }

  public removeScriptsFromHead(selector: string): void {
    const scriptElement = this.document.head.querySelector(selector) as HTMLLinkElement;

    if (scriptElement) {
      this.document.head.removeChild(scriptElement);
    }
  }

  private addExternalScriptToHead(renderer: Renderer2, scriptUrl: string): HTMLScriptElement {
    const script = renderer.createElement('script');
    script.src = scriptUrl;
    renderer.appendChild(this.document.head, script);
    return script;
  }

  private isExternalScriptLoaded(name: string): boolean {
    return !this.tools.isNil(this.loadedScripts()) && this.loadedScripts()[name] === true;
  }

  private createScriptsList(): void {
    this.headScripts = [];
    this.bodyScripts = [];
    const cookiebotScript = this.document.createElement('script');
    cookiebotScript.setAttribute('data-blockingmode', 'none');
    cookiebotScript.setAttribute('data-cbid', 'dca8ef39-6a1e-4898-aacb-67dad6d97125');
    cookiebotScript.setAttribute('id', 'Cookiebot');
    cookiebotScript.setAttribute('src', 'https://consent.cookiebot.com/uc.js');
    this.headScripts.push(cookiebotScript);

    const mainGtmScript = this.document.createElement('script');
    mainGtmScript.setAttribute('data-cookieconsent', 'ignore');
    mainGtmScript.innerHTML =
      "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':\n" +
      "        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],\n" +
      "      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=\n" +
      "      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);\n" +
      "    })(window,document,'script','dataLayer','" +
      this.appConfigService.appConfig.googleTagManagerId +
      "');";

    this.headScripts.push(mainGtmScript);
  }
}
